/**
 * Per-user access editor (AUTH-CHAT-WIRING-CONTRACT §2.4): role assignment +
 * permission overrides, rendered below the base user form on the user edit page.
 *
 * Loads `GET /admin/users/:id` for the user's current roles + overrides and the
 * permissions catalog (`GET /admin/permissions`) for the override key picker.
 * Saves via full-replace PUTs (`/admin/users/:id/roles`, `.../permissions`),
 * surfaces success/error through the shared notification provider, and
 * invalidates the `users` detail/list caches on save. Composes only existing
 * shadcn primitives + the central icon module.
 */
import * as React from "react";
import { useInvalidate, useNotification } from "@refinedev/core";
import { ROLES, type RoleName } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { humanize } from "@/resources/projects/_form";
import { CheckRow } from "@/resources/admin/_controls";
import {
  fetchAdminUser,
  fetchPermissionsCatalog,
  putUserPermissions,
  putUserRoles,
  type OverrideInput,
  type PermissionRow,
} from "@/lib/admin-api";

/** Local override draft (string `key` mirrors the PUT input shape). */
type OverrideDraft = OverrideInput;

export function UserAccessSections({ userId }: { userId: string }) {
  const notify = useNotification();
  const invalidate = useInvalidate();

  const [roles, setRoles] = React.useState<RoleName[]>([]);
  const [overrides, setOverrides] = React.useState<OverrideDraft[]>([]);
  const [catalog, setCatalog] = React.useState<PermissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [savingRoles, setSavingRoles] = React.useState(false);
  const [savingOverrides, setSavingOverrides] = React.useState(false);

  // New-override picker state.
  const [pendingKey, setPendingKey] = React.useState<string>("");

  React.useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetchAdminUser(userId, controller.signal),
      fetchPermissionsCatalog(controller.signal),
    ])
      .then(([user, perms]) => {
        setRoles(user.roles);
        setOverrides(
          user.permission_overrides.map((o) => ({
            key: o.permission_key,
            effect: o.effect,
          })),
        );
        setCatalog(perms);
      })
      .catch(() => {
        // Degrade quietly — the base form still works; access sections just
        // show empty defaults rather than crashing the edit page.
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [userId]);

  const toggleRole = (role: RoleName, next: boolean) => {
    setRoles((prev) =>
      next ? [...new Set([...prev, role])] : prev.filter((r) => r !== role),
    );
  };

  const saveRoles = async () => {
    setSavingRoles(true);
    try {
      const res = await putUserRoles(userId, roles);
      setRoles(res.roles);
      notify.open?.({
        type: "success",
        message: "Roles updated",
        key: `user-roles-${userId}`,
      });
      await invalidate({ resource: "users", invalidates: ["list", "detail"] });
    } catch (err) {
      notify.open?.({
        type: "error",
        message: "Failed to update roles",
        description: err instanceof Error ? err.message : String(err),
        key: `user-roles-err-${userId}`,
      });
    } finally {
      setSavingRoles(false);
    }
  };

  const addOverride = (effect: "grant" | "deny") => {
    if (!pendingKey) return;
    setOverrides((prev) => {
      const without = prev.filter((o) => o.key !== pendingKey);
      return [...without, { key: pendingKey, effect }];
    });
    setPendingKey("");
  };

  const removeOverride = (key: string) => {
    setOverrides((prev) => prev.filter((o) => o.key !== key));
  };

  const saveOverrides = async () => {
    setSavingOverrides(true);
    try {
      const res = await putUserPermissions(userId, overrides);
      setOverrides(
        res.overrides.map((o) => ({ key: o.permission_key, effect: o.effect })),
      );
      notify.open?.({
        type: "success",
        message: "Permission overrides updated",
        key: `user-perms-${userId}`,
      });
      await invalidate({ resource: "users", invalidates: ["list", "detail"] });
    } catch (err) {
      notify.open?.({
        type: "error",
        message: "Failed to update overrides",
        description: err instanceof Error ? err.message : String(err),
        key: `user-perms-err-${userId}`,
      });
    } finally {
      setSavingOverrides(false);
    }
  };

  const overriddenKeys = new Set(overrides.map((o) => o.key));
  const availableKeys = catalog.filter((p) => !overriddenKeys.has(p.key));

  return (
    <>
      {/* Roles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Roles</CardTitle>
          <CardDescription>
            Assign one or more RBAC roles. Roles grant baseline permissions;
            per-user overrides below refine them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
            {ROLES.map((role) => (
              <CheckRow
                key={role}
                checked={roles.includes(role)}
                disabled={loading}
                onCheckedChange={(next) => toggleRole(role, next)}
                label={humanize(role)}
                hint={role}
              />
            ))}
          </div>
        </CardContent>
        <CardFooter className="justify-end">
          <Button type="button" onClick={saveRoles} disabled={loading || savingRoles}>
            {savingRoles ? "Saving…" : "Save roles"}
          </Button>
        </CardFooter>
      </Card>

      {/* Permission overrides */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Permission overrides</CardTitle>
          <CardDescription>
            Per-user grants and denies layered over the role baseline. A{" "}
            <span className="font-medium">deny</span> always wins over a grant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {overrides.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No overrides — this user has only their role-based permissions.
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {overrides.map((o) => (
                <li
                  key={o.key}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <span className="font-mono text-xs">{o.key}</span>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={o.effect === "grant" ? "success" : "destructive"}
                    >
                      {o.effect}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Remove ${o.key}`}
                      onClick={() => removeOverride(o.key)}
                    >
                      <Icon name="delete" size={14} aria-hidden />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Add a new override from the catalog. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={pendingKey} onValueChange={setPendingKey}>
              <SelectTrigger className="sm:max-w-xs">
                <SelectValue placeholder="Add a permission key…" />
              </SelectTrigger>
              <SelectContent>
                {availableKeys.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    <span className="font-mono text-xs">{p.key}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!pendingKey}
                onClick={() => addOverride("grant")}
              >
                Grant
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!pendingKey}
                onClick={() => addOverride("deny")}
              >
                Deny
              </Button>
            </div>
          </div>
        </CardContent>
        <CardFooter className="justify-end">
          <Button
            type="button"
            onClick={saveOverrides}
            disabled={loading || savingOverrides}
          >
            {savingOverrides ? "Saving…" : "Save overrides"}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
}

export default UserAccessSections;
